
import React, { useEffect } from "react";
import { Separator } from "@/components/ui/separator";
import { getFishingDailyRate, calculateFishingPrice, calculateCampingPrice } from "@/utils/permitUtils";

interface PermitSummaryProps {
  permitType: string;
  days: number;
  campingDays: number;
  totalPrice: number;
}

export const PermitSummary: React.FC<PermitSummaryProps> = ({
  permitType,
  days,
  campingDays,
  totalPrice
}) => {
  // Log permit summary props whenever they change
  useEffect(() => {
    console.log("[PERMIT SUMMARY] Props received:", {
      permitType,
      days,
      campingDays,
      totalPrice
    });
  }, [permitType, days, campingDays, totalPrice]);

  // Calculate fishing permit price using our utility functions
  const getFishingPrice = () => {
    if (permitType.startsWith("daily")) {
      // For daily permits, use our utility function
      const dailyRate = getFishingDailyRate(permitType === "daily-all" ? "All Fisheries (Ages 14+)" : "Two Poles (Ages 14+)");
      const fishingPrice = calculateFishingPrice(permitType === "daily-all" ? "All Fisheries (Ages 14+)" : "Two Poles (Ages 14+)", days, 0);
      console.log(`[PERMIT SUMMARY] Calculated fishing price: ${dailyRate} × ${days} days = ${fishingPrice}`);
      return fishingPrice;
    } else {
      // Annual permits have fixed prices
      const annualPrice = permitType === "annual-individual" ? 150.00 : permitType === "annual-individual-two-poles" ? 180.00 : permitType === "annual-family" ? 200.00 : 220.00;
      console.log(`[PERMIT SUMMARY] Annual permit price: ${annualPrice}`);
      return annualPrice;
    }
  };

  // Calculate camping cost - Using campingDays directly for the number of nights
  const effectiveCampingNights = campingDays === 0 && permitType.startsWith("daily") ? 1 : campingDays;
  const campingTotal = campingDays > 0 ? calculateCampingPrice(effectiveCampingNights) : 0;

  // Calculate the actual total
  const calculatedTotal = getFishingPrice() + campingTotal;

  // Get daily rate for display
  const fishingDailyRate = permitType === "daily-all" ? 25 : permitType === "daily-two-poles" ? 40 : 0;

  // Log price breakdown for debugging
  console.log("[PERMIT SUMMARY] - Price breakdown:", {
    permitType,
    days,
    fishingPrice: getFishingPrice(),
    campingDays,
    effectiveCampingNights,
    campingTotal,
    calculatedTotal,
    providedTotal: totalPrice,
    match: Math.abs(calculatedTotal - totalPrice) < 0.01 ? "✓" : "✗"
  });

  // Check if this is a family permit
  const isFamilyPermit = permitType === "annual-family" || permitType === "annual-family-two-poles";
  
  return <div className="mb-8">
      <h3 className="font-semibold text-lg text-tribal-secondary mb-4">Summary</h3>
      
      <div className="space-y-2 text-sm">
        {/* Fishing permit cost display */}
        <div className="flex justify-between">
          <span>
            {permitType.startsWith("daily") ? `Fishing Permit (${days} day${days !== 1 ? 's' : ''} @ $${fishingDailyRate}/day)` : "Fishing Permit"}
          </span>
          <span>${getFishingPrice().toFixed(2)}</span>
        </div>
        
        {/* Camping add-on with clearer night calculation */}
        {permitType.startsWith("daily") && campingDays > 0 && <div className="flex justify-between">
            <span>
              Camping Add-on ({effectiveCampingNights} night{effectiveCampingNights !== 1 ? "s" : ""} @ $15/night)
            </span>
            <span>${campingTotal.toFixed(2)}</span>
          </div>}
        
        <div className="flex justify-between text-sm text-tribal-neutral">
          <span>Processing fee will be added at checkout</span>
          <span></span>
        </div>
        
        <Separator className="my-2" />
        
        <div className="flex justify-between font-bold text-base">
          <span>Total</span>
          <span>${calculatedTotal.toFixed(2)}</span>
        </div>

        {/* Display note about annual permits including dry camping */}
        {permitType.startsWith("annual") && 
          <div className="text-xs text-tribal-neutral mt-2 bg-tribal-primary/5 p-2 rounded">
            <ul className="list-disc pl-4 space-y-1">
              <li>Annual permits include general camping at non-electrical sites.</li>
              {isFamilyPermit && <li>Family Permit: Valid for spouses and their children (ages 17 and under). Children 18+ require their own permit.</li>}
            </ul>
          </div>
        }

        {/* Add an explanatory note about days vs nights */}
        {permitType.startsWith("daily") && campingDays > 0 && <div className="text-xs text-tribal-neutral mt-2">
            <p>Note: Fishing days include both start and end dates. Camping is charged per night between dates.</p>
          </div>}
      </div>
    </div>;
};
